import type { TrackerChatMessage } from '@/components/Messenger/types/chatMessageTypes';
import { coalesceAssistantMessageFromSources } from '@/components/Messenger/agent/assistantChatMessageMetadata';

/**
 * Сводит два упорядоченных списка сообщений с сохранением ссылок на неизменённые узлы.
 * Для ассистента метаданные объединяются через {@link coalesceAssistantMessageFromSources}
 * (серверный слот не затирается финалом SDK и наоборот).
 */
function stablePartFingerprint(part: Record<string, unknown>): string {
  const t = typeof part.type === 'string' ? part.type : '';
  if (t === 'text' || t === 'reasoning') {
    return `${t}:${typeof part.text === 'string' ? part.text : ''}`;
  }
  if (t === 'file') {
    return `file:${String(part.url ?? '')}:${String(part.filename ?? '')}:${String(part.mediaType ?? '')}`;
  }
  if (t.startsWith('tool-') || t === 'dynamic-tool') {
    try {
      return `${t}:${String(part.toolName ?? '')}:${String(part.state ?? '')}:${JSON.stringify(part.input ?? null)}:${JSON.stringify(part.output ?? null)}:${String(part.errorText ?? '')}`;
    } catch {
      return `${t}:json_error`;
    }
  }
  if (t.startsWith('data-')) {
    try {
      return `${t}:${JSON.stringify(part.data ?? null)}`;
    } catch {
      return `${t}:json_error`;
    }
  }
  try {
    return `${t}:${JSON.stringify(part)}`;
  } catch {
    return `${t}:unserializable`;
  }
}

function areMessagePartsEqual(
  left: TrackerChatMessage['parts'],
  right: TrackerChatMessage['parts']
): boolean {
  if (left === right) return true;
  const a = left ?? [];
  const b = right ?? [];
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const lp = a[i] as Record<string, unknown> | undefined;
    const rp = b[i] as Record<string, unknown> | undefined;
    if (lp === rp) continue;
    if (!lp || !rp) return false;
    if (stablePartFingerprint(lp) !== stablePartFingerprint(rp)) return false;
  }
  return true;
}

export function areTrackerChatMessagesEqual(
  left: TrackerChatMessage,
  right: TrackerChatMessage
): boolean {
  if (left === right) return true;
  if (left.id !== right.id || left.role !== right.role) return false;
  if ((left.metadata?.sendFailed ?? false) !== (right.metadata?.sendFailed ?? false)) return false;
  if ((left.metadata?.modelSlot ?? null) !== (right.metadata?.modelSlot ?? null)) return false;
  if ((left.metadata?.messageCreatedAt ?? null) !== (right.metadata?.messageCreatedAt ?? null))
    return false;
  return areMessagePartsEqual(left.parts, right.parts);
}

export function mergeStableTrackerChatMessages(
  prev: TrackerChatMessage[],
  incoming: TrackerChatMessage[]
): TrackerChatMessage[] {
  if (prev.length === 0) return incoming;

  const prevById = new Map(prev.map((item) => [item.id, item] as const));
  let changed = prev.length !== incoming.length;

  const next = incoming.map((item, index) => {
    const prevItem = prevById.get(item.id);
    const resolvedItem = prevItem ? coalesceAssistantMessageFromSources(prevItem, item) : item;
    if (prevItem && areTrackerChatMessagesEqual(prevItem, resolvedItem)) {
      if (prev[index] !== prevItem) changed = true;
      return prevItem;
    }
    changed = true;
    return resolvedItem;
  });

  if (!changed) {
    for (let i = 0; i < prev.length; i += 1) {
      if (prev[i] !== next[i]) {
        changed = true;
        break;
      }
    }
  }

  return changed ? next : prev;
}
